// SMART_NOTIFICATIONS — MVP polling (WebSocket lives in sprint 2).
//
// Polls /v1/notifications?unread=true every 15s. On new arrivals (delta
// vs the previous tick), fires a Sonner toast + a Web Audio sound.
// Backend handler runs in TenantTx so RLS keeps cross-tenant data out.
//
// API surface deliberately tiny: the bell consumes `useUnreadCount` and
// `useRecentNotifications`; the global side-effect (toast + sound) is
// activated once at the app root via `useNotificationsAutoToast`.
import { apiFetch } from '@/api/client'
import { type NotificationDTO, showNotification } from '@/lib/notify'
import { useAuthStore } from '@/stores/auth.store'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

const REFRESH_MS = 15_000

type EnvelopeList = { data: NotificationDTO[]; meta?: { total?: number } }
type EnvelopeCount = { data: { count: number } }

export function useRecentNotifications(onlyUnread = false, limit = 30) {
  const isAuthed = useAuthStore((s) => s.isAuthenticated)
  return useQuery({
    queryKey: ['notifications', 'list', { onlyUnread, limit }],
    enabled: isAuthed,
    refetchInterval: REFRESH_MS,
    refetchIntervalInBackground: false,
    queryFn: async () => {
      const qs = new URLSearchParams()
      if (onlyUnread) qs.set('unread', 'true')
      qs.set('limit', String(limit))
      const r = await apiFetch<EnvelopeList>(`/v1/notifications?${qs.toString()}`)
      return r.data
    },
  })
}

export function useUnreadCount() {
  const isAuthed = useAuthStore((s) => s.isAuthenticated)
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    enabled: isAuthed,
    refetchInterval: REFRESH_MS,
    refetchIntervalInBackground: false,
    queryFn: async () => {
      const r = await apiFetch<EnvelopeCount>('/v1/notifications/unread-count')
      return r.data.count
    },
  })
}

export function useMarkRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/v1/notifications/${id}/read`, { method: 'PATCH' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}

export function useMarkAllRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiFetch<void>('/v1/notifications/read-all', { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}

/**
 * Global side-effect — mount ONCE at the app root. On every poll tick,
 * compares the new unread set to the previous one; new ids → toast + sound.
 * No-op when not authenticated.
 */
export function useNotificationsAutoToast(): void {
  const isAuthed = useAuthStore((s) => s.isAuthenticated)
  const { data } = useRecentNotifications(true, 20)
  const { t } = useTranslation('common')
  const seenIds = useRef<Set<string>>(new Set())

  // Reset memory on logout — otherwise next login replays old IDs as "new".
  useEffect(() => {
    if (!isAuthed) seenIds.current = new Set()
  }, [isAuthed])

  useEffect(() => {
    if (!data) return
    // First tick: prime the set, do NOT toast (avoids spam on page load).
    if (seenIds.current.size === 0) {
      data.forEach((n) => seenIds.current.add(n.id))
      return
    }
    for (const n of data) {
      if (seenIds.current.has(n.id)) continue
      seenIds.current.add(n.id)
      showNotification(n, {
        t,
        action: n.href
          ? {
              label: t('notifications.view', 'Voir'),
              onClick: () => {
                window.location.href = n.href as string
              },
            }
          : undefined,
      })
    }
  }, [data, t])
}
